import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { queueRefetchInterval } from 'constants/queueRefetchInterval';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import { useChunkUpload } from 'hooks/useChunkUpload';
import i18next from 'i18next';
import {
  IInfinitePageParams,
  IInfinitePageStatisticsResponse,
} from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ChunkUploadParams,
  ChunkUploadResponse,
  IStatement,
  IStatementRows,
  IStatementsParam,
  IStatementsResponse,
  IStatementStatistics,
  IStatementUnrelatedRowsStatistics,
} from './types';

export const useGetStatementListInfinite = (params: IStatementsParam) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(
      ([_, value]) => value !== '' && value !== undefined
    )
  );

  return useInfiniteQuery<IStatement[], DefaultError>({
    queryKey: ['StatementPeriods', cleanParams],

    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement',
        params: { ...cleanParams, page: pageParam },
      });

      const value = await responseHandler<IStatementsResponse>(response, {
        groupName: `useGetStatementListInfinite - Page: ${pageParam}`,
      });

      return infiniteHandler(value);
    },

    initialPageParam: params.page,
    getNextPageParam: getNextPageParam,
    refetchInterval: queueRefetchInterval,
  });
};

export const useGetStatementDetail = (statementId: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IStatement, DefaultError>({
    queryKey: ['Statement', statementId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Statement/${statementId}`,
      });

      return responseHandler(response, {
        groupName: 'useGetStatementDetail',
        notFoundPath: '/Statement',
      });
    },
  });
};

export const useGetStatementRowsInfinite = (
  id: string,
  params?: IInfinitePageParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, setTotalPageCount, infiniteHandler } =
    useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<IStatementRows[], DefaultError>({
    queryKey: ['StatementRows', params, id],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement/Rows',
        params: { ...params, itemCount, page: pageParam, StatementId: id },
      });

      const value = await responseHandler<
        IInfinitePageStatisticsResponse<IStatementRows>
      >(response, {
        groupName: `useGetStatementRowsInfinite - ${pageParam}`,
      });

      return infiniteHandler(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};
export const useGetStatementSummaryRowsInfinite = (
  id: string,
  params?: IInfinitePageParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, setTotalPageCount, infiniteHandler } =
    useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<IStatementRows[], DefaultError>({
    queryKey: ['StatementSummaryRows', params, id],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement/SummaryRows',
        params: { ...params, itemCount, page: pageParam, StatementId: id },
      });

      const value = await responseHandler<
        IInfinitePageStatisticsResponse<IStatementRows>
      >(response, {
        groupName: `useGetStatementRowsInfinite - ${pageParam}`,
      });

      return infiniteHandler(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetStatementUnrelatedRowsInfinite = (
  id: string,
  params?: IInfinitePageParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, setTotalPageCount, infiniteHandler } =
    useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<IStatementRows[], DefaultError>({
    queryKey: ['StatementUnrelatedRows', params, id],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement/UnrelatedRows',
        params: { ...params, itemCount, page: pageParam, StatementId: id },
      });

      const value = await responseHandler<
        IInfinitePageStatisticsResponse<IStatementRows>
      >(response, {
        groupName: `useGetStatementUnrelatedRowsInfinite - ${pageParam}`,
      });

      return infiniteHandler(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};
export const useGetStatementUnrelatedRowsStatistics = (
  id: string,
  params?: IInfinitePageParams
) => {
  const { responseHandler } = useResponseHandler();

  return useQuery<IStatementUnrelatedRowsStatistics, DefaultError>({
    queryKey: ['StatementUnrelatedRowsStatistics', params, id],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement/UnrelatedRows',
        params: { ...params, itemCount: 1, page: 0, StatementId: id },
      });

      const value = await responseHandler<
        IInfinitePageStatisticsResponse<
          IStatementRows,
          IStatementUnrelatedRowsStatistics
        >
      >(response, {
        groupName: `useGetStatementUnrelatedRowsInfinite - ${pageParam}`,
      });
      return value.statistics;
    },
  });
};

export const useGetStatementStatistics = (params: IStatementsParam) => {
  const { responseHandler } = useResponseHandler();
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(
      ([_, value]) => value !== '' && value !== undefined
    )
  );
  return useQuery<IStatementStatistics, DefaultError>({
    queryKey: ['statement-statistics', cleanParams],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Statement`,
        params: {
          ...cleanParams,
          page: 0,
          itemCount: 1,
        },
      });

      const { statistics } = await responseHandler<
        IInfinitePageStatisticsResponse<IStatementRows, IStatementStatistics>
      >(response, {
        groupName: `useGetStatementStatistics`,
      });

      return statistics;
    },
    refetchInterval: queueRefetchInterval,
  });
};

export const usePOSTChunkedUpload = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePOSTChunkedUpload');

  const { retryUpload, uploadChunkedFiles } = useChunkUpload('Statement');

  return useMutation<ChunkUploadResponse, DefaultError, ChunkUploadParams>({
    mutationFn: async ({
      file,
      salesChannelId,
      providerId,
      settlementPeriodId,
      onProgress,
    }: ChunkUploadParams): Promise<ChunkUploadResponse> => {
      loading(
        i18next.t('service-message.upload.loading', {
          what: i18next.t('statements.popup.file'),
        })
      );

      const { totalChunks, importId } = await uploadChunkedFiles(
        file,
        onProgress
      );

      const finalRequestData = {
        importId,
        totalChunks,
        salesChannelId,
        providerId,
        settlementPeriodId,
      };

      const finalResponse = await apiClient({
        method: 'POST',
        url: 'Statement/Import/Complete',
        body: finalRequestData,
      });

      return responseHandler(finalResponse, {
        groupName: 'usePOSTChunkedUpload',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.upload.success', {
          what: i18next.t('statements.popup.file'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteStatement = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, success, dismiss } = useQueryToast('useDeleteStatement');

  return useMutation<void, DefaultError, string>({
    mutationFn: async (statementId: string) => {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('navbar.statement'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Statement/${statementId}`,
      });

      await responseHandler(response, {
        groupName: 'useDeleteStatement',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('navbar.statement'),
        })
      );
    },
    onError: dismiss,
  });
};
